import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useSite } from "@/context/SiteContext";
import { API, errMsg } from "@/lib/api";

export default function AdminBranding() {
  const { refresh } = useSite();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!settings) return <Skeleton className="h-96 rounded-xl" />;

  const branding = settings.branding || {};
  const business = settings.business || {};
  const social = settings.social || {};

  const setBranding = (k, v) => setSettings({ ...settings, branding: { ...branding, [k]: v } });
  const setBusiness = (k, v) => setSettings({ ...settings, business: { ...business, [k]: v } });
  const setSocial = (k, v) => setSettings({ ...settings, social: { ...social, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/settings", settings);
      await refresh();
      toast.success("Branding saved — the website has been updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-branding-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl tracking-tight">Branding & Logo</h1>
        <Button className="rounded-full" onClick={save} disabled={saving} data-testid="save-branding-btn">
          {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="logo-card">
          <CardContent className="p-6 space-y-6">
            <h2 className="font-display text-xl">Logo</h2>
            <p className="text-sm text-muted-foreground -mt-4">Your logo appears in the website header. Upload a dark-mode version if your logo is dark.</p>
            <div className="space-y-2">
              <Label>Logo (light mode)</Label>
              <div className="p-4 rounded-lg border bg-white">
                <ImageUpload value={branding.logo_light || ""} onChange={(v) => setBranding("logo_light", v)} label="logo" testId="logo-light-upload" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Logo (dark mode, optional)</Label>
              <div className="p-4 rounded-lg border bg-neutral-900">
                <ImageUpload value={branding.logo_dark || ""} onChange={(v) => setBranding("logo_dark", v)} label="dark logo" testId="logo-dark-upload" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Accent colour (optional)</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={branding.accent_color || "#4A5D4E"}
                  onChange={(e) => setBranding("accent_color", e.target.value)}
                  className="h-10 w-14 rounded border cursor-pointer"
                  data-testid="accent-color-picker"
                />
                <Button variant="outline" size="sm" onClick={() => setBranding("accent_color", "")} data-testid="reset-accent-btn">Reset to default</Button>
              </div>
              <p className="text-xs text-muted-foreground">Overrides the main button/highlight colour across the site.</p>
            </div>
            <div className="space-y-2">
              <Label>Accreditation badge (shown on homepage & footer)</Label>
              <ImageUpload value={branding.accreditation_badge || ""} onChange={(v) => setBranding("accreditation_badge", v)} label="badge" testId="accreditation-badge-upload" />
              <p className="text-xs text-muted-foreground">e.g. The Good Florist Guide badge.</p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card data-testid="business-card">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-display text-xl">Business identity</h2>
              <div className="space-y-2">
                <Label>Business name</Label>
                <Input value={business.name || ""} onChange={(e) => setBusiness("name", e.target.value)} data-testid="business-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Tagline</Label>
                <Input value={business.tagline || ""} onChange={(e) => setBusiness("tagline", e.target.value)} data-testid="business-tagline-input" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="social-card">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-display text-xl">Social links</h2>
              <div className="space-y-2">
                <Label>Facebook URL</Label>
                <Input placeholder="https://facebook.com/…" value={social.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} data-testid="social-facebook-input" />
              </div>
              <div className="space-y-2">
                <Label>Instagram URL</Label>
                <Input placeholder="https://instagram.com/…" value={social.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} data-testid="social-instagram-input" />
              </div>
              <div className="space-y-2">
                <Label>Twitter / X URL</Label>
                <Input placeholder="https://x.com/…" value={social.twitter || ""} onChange={(e) => setSocial("twitter", e.target.value)} data-testid="social-twitter-input" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
